import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isCefShell } from '../lib/platform';

// The CEF host's custom-scheme OAuth redirect. Keycloak must have this added as
// a Valid Redirect URI for the `ateliercode-app` client. The system browser
// hands the redirect to the OS, which delivers it back to the running host as an
// `ateliercode://` deep-link (App.tsx listens for the 'ateliercode://deep-link'
// CustomEvent and completes the PKCE exchange).
//
// The OAuth redirect_uri is an HTTPS landing page (not the bare ateliercode://
// scheme) so the external browser lands on a clean "you're signed in — you can
// close this tab" page instead of a dead custom-scheme navigation. That page
// (website/public/auth-complete.html, served at ateliercode.dev) re-fires
// `ateliercode://auth/callback?code=…` via a hidden iframe to hand the result
// back to the desktop app. This URL is used for BOTH the auth request and the
// token exchange, so the PKCE redirect_uri match holds.
const CEF_REDIRECT_URI = 'https://ateliercode.dev/auth-complete.html';

// Open a URL in the user's default system browser via the CEF host bridge
// ({"type":"open-external"} → host xdg-open). Used by desktop OAuth: the
// windowless OSR context can't host the Keycloak login page.
function openExternal(url: string): void {
  const cefQuery = (window as { cefQuery?: (opts: {
    request: string;
    onSuccess: (response: string) => void;
    onFailure: (errorCode: number, errorMessage: string) => void;
  }) => void }).cefQuery;
  if (!cefQuery) return;
  cefQuery({
    request: JSON.stringify({ type: 'open-external', url }),
    onSuccess: () => {},
    onFailure: (code, message) => {
      console.error('[Auth] open-external failed:', code, message);
    },
  });
}

// Keycloak OIDC configuration
const KEYCLOAK_URL = 'https://keycloak.unveiledsoftwaresolutions.com';
const KEYCLOAK_REALM = 'ateliercode';
const KEYCLOAK_CLIENT_ID = 'ateliercode-app';
const KEYCLOAK_BASE = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect`;

export interface User {
  id: string;
  email: string;
  name: string;
  username?: string;
  roles?: string[];
  avatarUrl?: string;
}

interface TokenSet {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
  expiresAt: number; // Unix timestamp in ms
}

interface AuthState {
  // Auth state
  isAuthenticated: boolean;
  user: User | null;
  token: string | null; // Access token (for backward compat with serverConnection)
  tokens: TokenSet | null;

  // PKCE state
  codeVerifier: string | null;

  // UI state
  showAuthDialog: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setShowAuthDialog: (show: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Auth actions
  login: () => void;
  logout: () => void;
  handleCallback: (code: string, redirectUri?: string) => Promise<boolean>;
  checkAuth: () => Promise<boolean>;
  refreshAccessToken: () => Promise<boolean>;
  /** Ensure we have a valid (non-expired) token, refreshing if needed */
  ensureFreshToken: () => Promise<string | null>;
}

// PKCE helpers
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(buffer: Uint8Array): string {
  let str = '';
  for (const byte of buffer) {
    str += String.fromCharCode(byte);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function parseJwt(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload);
}

// Known valid redirect URIs registered in Keycloak
const KNOWN_VALID_URIS = [
  'https://tauri.localhost/',
  'tauri://localhost/',
  'http://localhost:1420/',
  'https://app.ateliercode.dev/',
];

function getRedirectUri(): string {
  // CEF desktop host: Keycloak can't redirect to app://ateliercode/ (rejected as
  // an unsupported origin), so we use a custom-scheme redirect that the OS routes
  // back into the running app as an ateliercode:// deep-link.
  if (isCefShell()) {
    return CEF_REDIRECT_URI;
  }

  const origin = window.location.origin;

  // Log for debugging OAuth issues
  console.log('[Auth] window.location.origin:', origin);

  // Tauri v2 uses https://tauri.localhost
  if (origin.includes('tauri.localhost')) {
    return 'https://tauri.localhost/';
  }
  // Tauri v1 uses tauri://localhost
  if (origin.startsWith('tauri://')) {
    return 'tauri://localhost/';
  }
  // Web/dev fallback
  return `${origin}/`;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      token: null,
      tokens: null,
      codeVerifier: null,
      showAuthDialog: false,
      isLoading: false,
      error: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setToken: (token) => set({ token }),
      setShowAuthDialog: (showAuthDialog) => set({ showAuthDialog }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      login: async () => {
        const redirectUri = getRedirectUri();
        const cef = isCefShell();

        // Pre-validate redirect URI to avoid Keycloak error page with no dismiss
        // option. The CEF host uses the custom-scheme redirect (CEF_REDIRECT_URI),
        // which isn't a web origin and so isn't in KNOWN_VALID_URIS — skip this
        // web-only guard for the desktop flow.
        if (!cef) {
          const isValidUri = KNOWN_VALID_URIS.some(uri =>
            redirectUri === uri || redirectUri.startsWith(uri.slice(0, -1))
          );

          if (!isValidUri) {
            // Unknown redirect URI — show error in our UI instead of navigating to Keycloak
            set({
              error: `OAuth not supported for origin: ${redirectUri}. Please use the desktop app or web version at app.ateliercode.dev.`,
              showAuthDialog: true,
            });
            return;
          }
        }

        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        // Store code_verifier for the callback
        set({ codeVerifier });

        const params = new URLSearchParams({
          client_id: KEYCLOAK_CLIENT_ID,
          response_type: 'code',
          scope: 'openid email profile offline_access',
          redirect_uri: redirectUri,
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
        });

        const authUrl = `${KEYCLOAK_BASE}/auth?${params.toString()}`;

        if (cef) {
          // Desktop (CEF host): the windowless OSR context can't host the
          // Keycloak login page, so open the auth URL in the system browser via
          // the host bridge. Keycloak redirects to CEF_REDIRECT_URI
          // (ateliercode://auth/callback?code=...&state=...), the OS hands that
          // back to the running app as an 'ateliercode://deep-link' CustomEvent
          // (App.tsx) → handleCallback() does the PKCE token exchange. The
          // codeVerifier set above is persisted so the exchange can complete.
          openExternal(authUrl);
          return;
        }

        // Web: run the standard browser redirect flow: navigate to Keycloak,
        // which redirects back with `?code=` (handled at app boot in App.tsx) →
        // handleCallback() does the PKCE token exchange.
        window.location.href = authUrl;
      },

      handleCallback: async (code: string, redirectUri?: string) => {
        const { codeVerifier } = get();
        if (!codeVerifier) {
          set({ error: 'Missing PKCE code verifier' });
          return false;
        }

        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${KEYCLOAK_BASE}/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              grant_type: 'authorization_code',
              client_id: KEYCLOAK_CLIENT_ID,
              code,
              redirect_uri: redirectUri || getRedirectUri(),
              code_verifier: codeVerifier,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error_description || 'Token exchange failed');
          }

          const data = await response.json();

          // Parse user info from ID token
          const claims = parseJwt(data.access_token);
          const user: User = {
            id: claims.sub as string,
            email: (claims.email as string) || '',
            name: (claims.name as string) || (claims.preferred_username as string) || '',
            username: claims.preferred_username as string,
            roles: (claims.realm_access as { roles?: string[] })?.roles || [],
          };

          const tokens: TokenSet = {
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            idToken: data.id_token,
            expiresAt: Date.now() + data.expires_in * 1000,
          };

          set({
            isAuthenticated: true,
            user,
            token: data.access_token,
            tokens,
            codeVerifier: null,
            showAuthDialog: false,
            isLoading: false,
          });

          // Schedule token refresh
          scheduleRefresh(data.expires_in);

          return true;
        } catch (error) {
          set({
            isLoading: false,
            codeVerifier: null,
            error: error instanceof Error ? error.message : 'Authentication failed',
          });
          return false;
        }
      },

      logout: () => {
        const { tokens } = get();
        const idTokenHint = tokens?.idToken;

        set({
          isAuthenticated: false,
          user: null,
          token: null,
          tokens: null,
          codeVerifier: null,
          error: null,
        });

        // Redirect to Keycloak logout
        if (idTokenHint) {
          const params = new URLSearchParams({
            id_token_hint: idTokenHint,
            post_logout_redirect_uri: getRedirectUri(),
            client_id: KEYCLOAK_CLIENT_ID,
          });
          window.location.href = `${KEYCLOAK_BASE}/logout?${params.toString()}`;
        }
      },

      checkAuth: async () => {
        const { tokens } = get();
        if (!tokens) {
          set({ isAuthenticated: false, user: null, token: null });
          return false;
        }

        // Check if token is expired or about to expire (within 30s)
        if (tokens.expiresAt < Date.now() + 30000) {
          // Try to refresh
          const refreshed = await get().refreshAccessToken();
          if (!refreshed) {
            set({ isAuthenticated: false, user: null, token: null, tokens: null });
            return false;
          }
        }

        // Token is valid, schedule refresh
        const timeUntilExpiry = Math.max(0, (tokens.expiresAt - Date.now()) / 1000);
        scheduleRefresh(timeUntilExpiry);
        return true;
      },

      refreshAccessToken: async () => {
        const { tokens } = get();
        if (!tokens?.refreshToken) {
          return false;
        }

        try {
          const response = await fetch(`${KEYCLOAK_BASE}/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              grant_type: 'refresh_token',
              client_id: KEYCLOAK_CLIENT_ID,
              refresh_token: tokens.refreshToken,
            }),
          });

          if (!response.ok) {
            return false;
          }

          const data = await response.json();

          const claims = parseJwt(data.access_token);
          const user: User = {
            id: claims.sub as string,
            email: (claims.email as string) || '',
            name: (claims.name as string) || (claims.preferred_username as string) || '',
            username: claims.preferred_username as string,
            roles: (claims.realm_access as { roles?: string[] })?.roles || [],
          };

          const newTokens: TokenSet = {
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            idToken: data.id_token,
            expiresAt: Date.now() + data.expires_in * 1000,
          };

          set({
            isAuthenticated: true,
            user,
            token: data.access_token,
            tokens: newTokens,
          });

          scheduleRefresh(data.expires_in);
          return true;
        } catch {
          return false;
        }
      },

      ensureFreshToken: async () => {
        const { tokens, refreshAccessToken } = get();
        if (!tokens) return null;

        // If token expires within 60 seconds, refresh it
        if (tokens.expiresAt < Date.now() + 60000) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) return null;
        }

        return get().token;
      },
    }),
    {
      name: 'ateliercode-auth',
      version: 3,
      partialize: (state) => ({
        // Tokens ARE persisted (since v3) so app updates / restarts don't
        // bounce the user back to the login screen. Refresh tokens are
        // designed to be persisted — that's the entire point of OAuth's
        // refresh-grant flow. The XSS risk is real on the web build but
        // the UX cost of forcing a manual relogin every desktop update
        // outweighs it; we mitigate by relying on Keycloak's session +
        // refresh-token rotation. If we add a stricter policy later
        // (e.g. OS keychain on desktop, sessionStorage on web) it can
        // swap in here without changing the auth flow.
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        codeVerifier: state.codeVerifier,
        tokens: state.tokens,
        token: state.token,
      }),
      migrate: (persisted, version) => {
        const state = persisted as Record<string, unknown>;
        if (version < 2) {
          // v1 had a different token shape — drop and force a re-login.
          delete state.token;
          delete state.tokens;
        }
        // v2 → v3: existing v2 records have no persisted tokens (they
        // weren't in partialize before), so the user will hit the auth
        // flow once after upgrade and from then on stay logged in.
        return state;
      },
    }
  )
);

// Token refresh scheduler
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRefresh(expiresInSeconds: number) {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }
  // Refresh 60 seconds before expiry
  const refreshIn = Math.max(0, (expiresInSeconds - 60)) * 1000;
  refreshTimer = setTimeout(() => {
    useAuthStore.getState().refreshAccessToken();
  }, refreshIn);
}

// ── Auto-rehydrate: when store loads from localStorage, re-check auth ──
// Use a small delay to let zustand persist finish hydrating
setTimeout(() => {
  const state = useAuthStore.getState();
  if (state.tokens?.refreshToken) {
    // We have persisted tokens — check and refresh if needed
    state.checkAuth().catch(() => {
      // Refresh failed (e.g. offline), will retry on visibility change
    });
  }
}, 100);

// ── Visibility-based refresh: refresh token when app comes to foreground ──
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const state = useAuthStore.getState();
      if (state.tokens?.refreshToken) {
        state.ensureFreshToken().catch(() => {
          // Will retry on next visibility change or health check
        });
      }
    }
  });
}
